// Lectura de archivos Excel/CSV en el navegador (SheetJS). La validación y la aplicación
// se hacen en el servidor: aquí solo se convierten las hojas en filas con los campos esperados.
import * as XLSX from 'xlsx';
import type { CatalogImportRow } from '@teo/shared';

export interface ParsedCatalog {
  rows: CatalogImportRow[];
  /** Hojas leídas y filas por hoja (para mostrar al usuario). */
  sheets: { name: string; rows: number }[];
  /** Hojas ignoradas por no tener el encabezado esperado. */
  skipped: string[];
  /** Referencias que aparecen en más de una hoja: se combinan (la primera hoja manda, las demás completan vacíos). */
  merged: number;
}

type Cell = string | number | boolean | Date | null | undefined;
type Row = Cell[];

/** Normaliza un encabezado: sin acentos, minúsculas, espacios simples. */
export function normHeader(v: Cell): string {
  return String(v ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

// Encabezados aceptados (español del cliente y equivalentes en inglés).
const FIELD_HEADERS: Record<keyof Omit<CatalogImportRow, 'row'>, string[]> = {
  ref: ['codigo', 'código', 'referencia (codigo)', 'ref', 'code', 'sku'],
  name: ['referencia', 'nombre', 'producto', 'name', 'product', 'reference'],
  brand: ['marca', 'brand'],
  category: ['categoria', 'tipo', 'category', 'type'],
  gender: ['genero', 'gender'],
  unit_cost: ['costo', 'cost', 'unit cost'],
  internal_price: ['distribuidor', 'interno', 'precio interno', 'local', 'internal', 'distributor', 'internal price'],
  wholesale_price: ['por mayor', 'mayorista', 'mayor', 'wholesale', 'wholesale price'],
  retail_price: ['detal', 'al detal', 'retail', 'retail price'],
  promo_price: ['promocion', 'promo', 'promotion', 'promo price'],
};

function fieldFor(header: string): keyof Omit<CatalogImportRow, 'row'> | null {
  const h = normHeader(header);
  if (!h) return null;
  for (const [field, names] of Object.entries(FIELD_HEADERS) as [keyof Omit<CatalogImportRow, 'row'>, string[]][]) {
    if (names.includes(h)) return field;
  }
  return null;
}

function toNumber(v: Cell): number | null {
  if (v === null || v === undefined || v === '') return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const s = String(v).replace(/[$\s]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function toRef(v: Cell): string {
  if (typeof v === 'number') return String(Math.trunc(v));
  return String(v ?? '').trim();
}

/**
 * Detecta el encabezado dentro de las primeras 10 filas. Soporta el formato del listado real del cliente
 * (fila 1: Codigo · Referencia · Costo · "Precio venta" combinado; fila 2: Distribuidor · Por mayor · Detal · Promocion)
 * y el formato simple de una sola fila de encabezado.
 */
function detectHeader(rows: Row[]): { start: number; map: Map<number, keyof Omit<CatalogImportRow, 'row'>> } | null {
  for (let i = 0; i < Math.min(rows.length, 10); i++) {
    const map = new Map<number, keyof Omit<CatalogImportRow, 'row'>>();
    const used = new Set<string>();
    const line = rows[i] ?? [];
    // Cada campo se toma de su primera columna (una hoja puede repetir bloques de columnas).
    line.forEach((cell, col) => {
      const f = fieldFor(String(cell ?? ''));
      if (f && !used.has(f)) {
        map.set(col, f);
        used.add(f);
      }
    });
    if (!used.has('ref')) continue;
    // Segunda fila de encabezado (listas de precio bajo "Precio venta").
    const next = rows[i + 1] ?? [];
    let usedNext = false;
    next.forEach((cell, col) => {
      const f = fieldFor(String(cell ?? ''));
      if (f && !map.has(col) && !used.has(f)) {
        map.set(col, f);
        used.add(f);
        usedNext = true;
      }
    });
    if ([...map.values()].includes('ref')) return { start: i + (usedNext ? 2 : 1), map };
  }
  return null;
}

export function parseCatalogWorkbook(data: ArrayBuffer | Uint8Array): ParsedCatalog {
  const wb = XLSX.read(data, { type: 'array' });
  const out: ParsedCatalog = { rows: [], sheets: [], skipped: [], merged: 0 };
  const byRef = new Map<string, { row: CatalogImportRow; sheet: string }>();
  for (const name of wb.SheetNames) {
    const ws = wb.Sheets[name];
    if (!ws) continue;
    const rows = XLSX.utils.sheet_to_json<Row>(ws, { header: 1, raw: true, defval: null });
    const header = detectHeader(rows);
    if (!header) {
      out.skipped.push(name);
      continue;
    }
    const gender = /dama|mujer|women/i.test(name) ? 'Dama' : /masculino|hombre|men/i.test(name) ? 'Hombre' : undefined;
    let count = 0;
    for (let r = header.start; r < rows.length; r++) {
      const line = rows[r] ?? [];
      const row: CatalogImportRow = { row: r + 1, ref: '' };
      for (const [col, field] of header.map) {
        const v = line[col];
        if (field === 'ref') row.ref = toRef(v);
        else if (field === 'name' || field === 'brand' || field === 'category' || field === 'gender') {
          const s = String(v ?? '').trim();
          if (s) row[field] = s;
        } else row[field] = toNumber(v);
      }
      if (!row.ref) continue;
      if (gender && !row.gender) row.gender = gender;
      const seen = byRef.get(row.ref);
      if (seen && seen.sheet !== name) {
        // Misma referencia en otra hoja (p. ej. hoja de promociones): completa solo los campos vacíos.
        for (const k of Object.keys(row) as (keyof CatalogImportRow)[]) {
          if (k === 'row' || k === 'ref') continue;
          const v = row[k];
          if (v !== null && v !== undefined && (seen.row[k] === null || seen.row[k] === undefined)) {
            (seen.row as Record<string, unknown>)[k] = v;
          }
        }
        out.merged++;
        count++;
        continue;
      }
      if (!seen) byRef.set(row.ref, { row, sheet: name });
      out.rows.push(row);
      count++;
    }
    out.sheets.push({ name, rows: count });
  }
  return out;
}
